import { ymdLocal } from '../lib/format.js';
import { isoMondayOf } from '../lib/schedule.js';

// The schedule builder's top bar: week/day navigation on the left, the
// view controls and week-level actions on the right.
//
// Extracted verbatim from BuildScheduleView (2026-08-23) — that file was at
// its recorded line ceiling and could not take the Swap control. Purely
// presentational: every piece of state still lives in the parent.
export function ScheduleToolbar({
  viewMode, setViewMode, isNarrow,
  weekStart, setWeekStart, thisMon, dayDate, setDayDate,
  shiftWeek, shiftDay, location,
  showAsAvailability, setShowAsAvailability,
  openCreateForCell, setTemplateUI,
  clearWeek, clearBusy, shifts,
  publishBusy, setPublishConfirmText, setPublishConfirmOpen,
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {viewMode === 'week' ? (
          <>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(-1)}>← Prev</button>
            <button className="btn-secondary" onClick={() => setWeekStart(thisMon)} disabled={weekStart === thisMon}
                    style={{ padding: '6px 12px', opacity: weekStart === thisMon ? 0.5 : 1 }}>This week</button>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(1)}>Next →</button>
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, color: '#37474f' }}>
              Week of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' · '}{location.name}
            </span>
          </>
        ) : (
          <>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftDay(-1)}>← Prev</button>
            <button className="btn-secondary" onClick={() => { const t = ymdLocal(new Date()); setDayDate(t); setWeekStart(isoMondayOf(new Date())); }}
                    style={{ padding: '6px 12px' }}>Today</button>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftDay(1)}>Next →</button>
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, color: '#37474f' }}>
              {new Date(dayDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              {' · '}{location.name}
            </span>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {/* Round 16: Week / Day toggle. Hidden on narrow screens — the
            7-day grid doesn't fit on a phone in portrait, so day-only. */}
        {!isNarrow && (
          <div style={{ display: 'flex', border: '1px solid #d0d4d9', borderRadius: 6, overflow: 'hidden' }}>
            {[{ id: 'week', l: 'Week' }, { id: 'day', l: 'Day' }].map((m) => (
              <button key={m.id} onClick={() => setViewMode(m.id)}
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                          background: viewMode === m.id ? 'var(--forest)' : '#fff',
                          color: viewMode === m.id ? '#fff' : '#37474f',
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                {m.l}
              </button>
            ))}
          </div>
        )}
        {/* Availability ↔ Restrictions toggle (CJ 2026-06-15). Inverts
            the cell shading: by default the grid highlights when staff
            CAN'T work (red/yellow). Flipping to Availability paints
            free cells soft green and fades blocked cells to gray. */}
        <button onClick={() => setShowAsAvailability(v => !v)}
          title={showAsAvailability
            ? 'Showing when staff are available. Click to switch to restrictions.'
            : 'Showing when staff are restricted. Click to switch to availability.'}
          style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600,
                    background: showAsAvailability ? '#dcfce7' : '#fff',
                    color: showAsAvailability ? '#166534' : '#37474f',
                    border: `1px solid ${showAsAvailability ? '#86efac' : '#d0d4d9'}`,
                    borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          {showAsAvailability ? '🟢 Availability' : '⛔ Restrictions'}
        </button>
        <button className="btn-secondary" onClick={() => openCreateForCell(null, viewMode === 'day' ? dayDate : weekStart)}
          style={{ fontSize: 12 }}>+ Open shift</button>
        <button onClick={() => setTemplateUI('list')} className="btn-secondary" style={{ fontSize: 12 }}>
          Templates
        </button>
        {/* CJ 2026-06-16: Monarch is the schedule source of truth; the
            pull-from-Square affordance is gone (endpoint still 410s as a
            tombstone). Staff-facing daily Gantt (Home → Daily) still reads
            Square via the /api/schedule read endpoints. */}
        <button onClick={clearWeek} disabled={clearBusy || shifts.length === 0}
          title={shifts.length === 0 ? 'Nothing to clear' : 'Delete every shift in this week'}
          style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    background: '#fff', color: '#c62828',
                    border: '1px solid #ffcdd2', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                    opacity: (clearBusy || shifts.length === 0) ? 0.5 : 1 }}>
          {clearBusy ? 'Clearing…' : 'Clear week'}
        </button>
        <button onClick={() => { setPublishConfirmText(''); setPublishConfirmOpen(true); }}
          disabled={publishBusy || !shifts.some((s) => s.status === 'draft')}
          title={shifts.some((s) => s.status === 'draft') ? 'Publish all drafts in this week' : 'No drafts to publish'}
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700,
                    background: 'var(--forest)', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                    opacity: (publishBusy || !shifts.some((s) => s.status === 'draft')) ? 0.5 : 1 }}>
          {publishBusy ? 'Publishing…' : 'Publish week'}
        </button>
      </div>
    </div>
  );
}
